import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

import { axisValueToGrade, GRADE_ORDER } from "../../utils/examScoring";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

/**
 * Draws the dashed "Recommended Pass" marker. Chart.js has no annotation
 * support without the plugin package, so the line is painted directly.
 */
function createPassLinePlugin(value, label) {
  return {
    id: "otsPassLine",
    afterDatasetsDraw(chart) {
      if (value === null || value === undefined) return;

      const scale = chart.scales.y;
      const y = scale && scale.getPixelForValue(value);
      if (!Number.isFinite(y)) return;

      const { ctx, chartArea } = chart;
      ctx.save();
      ctx.setLineDash([6, 6]);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = "#94a3b8";
      ctx.beginPath();
      ctx.moveTo(chartArea.left, y);
      ctx.lineTo(chartArea.right, y);
      ctx.stroke();

      ctx.setLineDash([]);
      ctx.font = "600 10px Inter, Segoe UI, sans-serif";
      ctx.fillStyle = "#94a3b8";
      ctx.textAlign = "right";
      ctx.textBaseline = "bottom";
      ctx.fillText(label || "", chartArea.right - 4, y - 4);
      ctx.restore();
    },
  };
}

/**
 * One chart for both tracker modes: raw scores (JAMB out of 400, POST UTME out
 * of 100) and WAEC/BECE grade progression, where the y axis is the grade ladder.
 */
const MockProgressChart = ({
  mode = "score",
  points = [],
  accent = "#006b3f",
  max = 100,
  passMark = null,
  passLabel = "",
}) => {
  const isGrade = mode === "grade";

  const data = useMemo(() => {
    const labels = points.map((point) => point.label);
    const values = points.map((point) => point.value);

    return {
      labels,
      datasets: [
        {
          data: values,
          borderColor: accent,
          borderWidth: 2.5,
          tension: 0.35,
          fill: true,
          backgroundColor: (context) => {
            const { ctx, chartArea } = context.chart;
            if (!chartArea) return "rgba(148, 163, 184, 0.25)";
            const gradient = ctx.createLinearGradient(
              0,
              chartArea.top,
              0,
              chartArea.bottom
            );
            if (isGrade) {
              gradient.addColorStop(0, "rgba(148, 163, 184, 0.28)");
              gradient.addColorStop(1, "rgba(148, 163, 184, 0.06)");
            } else {
              gradient.addColorStop(0, "rgba(71, 85, 105, 0.55)");
              gradient.addColorStop(1, "rgba(71, 85, 105, 0.12)");
            }
            return gradient;
          },
          pointBackgroundColor: points.map((point) => point.color || accent),
          pointBorderColor: "#fff",
          pointBorderWidth: 2.5,
          pointRadius: 5,
          pointHoverRadius: 7,
        },
      ],
    };
  }, [points, accent, isGrade]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 12, right: 8, bottom: 0, left: 0 } },
      interaction: { intersect: false, mode: "index" },
      scales: {
        y: isGrade
          ? {
              min: 0,
              max: GRADE_ORDER.length - 1,
              ticks: {
                stepSize: 1,
                color: "#64748b",
                font: { size: 10, weight: "600" },
                callback: (value) => axisValueToGrade(value),
              },
              grid: { color: "#e8edf2", borderDash: [4, 4], drawBorder: false },
            }
          : {
              beginAtZero: true,
              max,
              ticks: {
                color: "#94a3b8",
                font: { size: 10 },
                maxTicksLimit: 5,
              },
              grid: { display: false, drawBorder: false },
            },
        x: {
          ticks: {
            color: "#94a3b8",
            font: { size: 10 },
            maxRotation: 0,
            autoSkipPadding: 12,
          },
          grid: { display: false, drawBorder: false },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#0f172a",
          padding: 10,
          displayColors: false,
          titleFont: { size: 11, weight: "600" },
          bodyFont: { size: 12, weight: "700" },
          callbacks: {
            title: (items) => points[items[0].dataIndex]?.tooltipTitle || "",
            label: (item) => points[item.dataIndex]?.tooltipLabel || "",
          },
        },
      },
    }),
    [isGrade, max, points]
  );

  const plugins = useMemo(
    () => [createPassLinePlugin(passMark, passLabel)],
    [passMark, passLabel]
  );

  return (
    <div className="tracker__chart-canvas">
      <Line data={data} options={options} plugins={plugins} />
    </div>
  );
};

export default MockProgressChart;
